import type { RequestOptions } from "@/api/client";
export interface SeedingConfig {
  seed_from: "none" | "manual" | "series" | "ranking_csv";
  series_id: string | null;
  ranking_import_id: string | null;
  method: "sequential" | "snake" | "reverse" | "circle" | "circle_snake" | "random";
  unranked: "random" | "entry_order";
  apply_up_category_factor: boolean;
}

export interface RoundTransport {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
  delete?: <T>(path: string, options?: RequestOptions<T>) => Promise<T>;
}
export interface WorkspaceProps {
  competitionId: string;
  principalKey: string;
  competitionVersion: number;
  revision?: string | number;
  roles: readonly string[];
  rulesAllowed: boolean;
  rulesEnabled: boolean;
  manualEnabled: boolean;
  transport?: RoundTransport;
  onSaved: () => Promise<void>;
}
export interface RoundHeat {
  locked?: boolean;
  locked_for_judges?: boolean;
  id: string;
  name: string;
  status: string;
  round_id: string;
  division_id: string;
  area_id: string | null;
  version: number;
  sort_order: number;
}
export interface RoundRider {
  id: string;
  heat_id: string;
  rider_id?: string | null;
  name: string;
  seed: number | null;
  status: string;
  group_division_id?: string | null;
  version: number;
}
export interface Rule {
  id?: string;
  sort_order: number;
  from_places: string;
  by: "heat" | "round";
  to_round_id: string;
  seeding: string;
  target_heat_count: number | null;
}
export interface RoundRow {
  id: string;
  division_id: string;
  name: string;
  sort_order: number;
  stage: number;
  stage_order: number;
  status: string;
  version: number;
  run_order: string;
  criteria_set_id: string | null;
  scoring_carry: string;
  drop_lowest_rounds: number;
  multi_heat_combine: string;
  seed_tie_back: string[];
  heat_duration_seconds: number | null;
  format: Record<string, unknown> | null;
  resolved_format?: Record<string, unknown>;
  seeding?: SeedingConfig | null;
  seeding_summary?: SeedingConfig & {
    round_id: string;
    label: string;
    manual_ready: boolean;
    seeds?: Record<string, number>;
    instruction?: string;
  };
  affected_scores?: { heat_id: string; heat_name?: string; name?: string; score_count: number }[];
  progression_may_change?: boolean;
  progression_changed_heat_ids?: string[];
  heats: RoundHeat[];
  riders?: RoundRider[];
  rules?: Rule[];
  byes?: string[];
  progression?: {
    applied: boolean;
    can_undo: boolean;
    promoted_count: number;
    scored_heat_ids: string[];
  };
}
export interface Advance {
  heat_rider_id: string;
  from_heat_id: string;
  rank: number;
  name: string | null;
  reason: string;
}
export interface PreviewRule {
  rule_id: string;
  to_round_id: string;
  to_round_name?: string;
  from_places?: string;
  by?: "heat" | "round";
  target_heat_count: number;
  advancing: Advance[];
  seeding: {
    heat_rider_id: string;
    target_heat_index: number;
    heat_id?: string | null;
    seed: number;
    sort_order: number;
  }[];
}
export interface ProgressionPreview {
  round_id: string;
  version: number;
  preview_token: string;
  rules: PreviewRule[];
  blocked: { reason: string; detail: Record<string, unknown> }[];
  target_orders?: {
    round_id: string;
    heat_index: number;
    source_keys: string[];
    entries?: { source_key: string; name: string; seed: number | null; existing: boolean }[];
  }[];
}
export interface PlannedRound {
  key: string;
  name: string;
  sort_order: number;
  stage: number;
  stage_order: number;
  heat_count: number;
  heat_sizes: number[];
  run_order: string;
}
export interface RoundPlan {
  entry_count: number;
  verification: "unverified";
  source: string;
  rounds: PlannedRound[];
  rules: {
    from: string;
    to: string;
    from_places: string;
    by: "heat" | "round";
    seeding: string;
    target_heat_count: number;
  }[];
}
export function ordinal(n: number) {
  const last = n % 100;
  return `${n}${last >= 11 && last <= 13 ? "th" : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th"}`;
}
export function ruleSummary(from: string, by: string, target: string) {
  if (from === "rest")
    return `Remaining riders from ${by === "heat" ? "each heat" : "the round"} → ${target}`;
  const places = from.endsWith("%")
    ? `Top ${from}`
    : from
        .split(",")
        .map((part) =>
          part
            .split("-")
            .map((n) => ordinal(Number(n)))
            .join("–"),
        )
        .join(", ");
  return `${places} from ${by === "heat" ? "each heat" : "the round"} → ${target}`;
}
